import AHttpService from '@d-services/a-http-service/a-http-service';
import { addObjectToFormData } from '@d-shared/forms/forms';
import URLS from '@d-shared/urls/urls';
import LocalStorage from '@d-shared/utils/local-storage';

const ADDED_TO_BOOKSHELF_KEY = 'added-to-bookshelf';
const ALLOWED_USER_ENTRY_TYPES = [
  'all',
  'currently_reading',
  'read',
  'reviewed',
  'want_to_read',
];

class BookshelfService extends AHttpService {
  async addEntry(entryData, shouldSaveInLocalStorage = true) {
    const formData = new FormData();
    addObjectToFormData(entryData, formData);

    const response = await this._http.post(URLS.api.library.bookshelf.entries, formData);

    if (shouldSaveInLocalStorage) {
      this.saveJustAddedBookshelfEntry(response.data);
    }

    return response.data;
  }

  async trackAddingEntry() {
    const response = await this._http.post(URLS.api.library.bookshelf.track);
    return response.data;
  }

  async deleteEntry(entrySlug) {
    const url = URLS.api.library.bookshelf.entry.replace('<entry-slug>', entrySlug);
    const response = await this._http.delete(url);
    return response.data;
  }

  getBookshelfOptionText(optionValue) {
    switch (optionValue) {
    case 'want_to_read':
      return 'Want to read';
    case 'currently_reading':
      return 'Currently reading';
    case 'read':
      return 'Read';
    default:
      return null;
    }
  }

  getBookshelvesOptions(withAllOption = false) {
    const bookshelvesOptions = [
      {
        text: 'Want to read',
        value: 'want_to_read',
      },
      {
        text: 'Currently reading',
        value: 'currently_reading',
      },
      {
        text: 'Read',
        value: 'read',
      },
    ];

    if (withAllOption) {
      bookshelvesOptions.unshift({
        text: 'All',
        value: 'all',
      });
    }

    return bookshelvesOptions;
  }

  async getEntry(entrySlug) {
    const url = URLS.api.library.bookshelf.entry.replace('<entry-slug>', entrySlug);
    const response = await this._http.get(url);
    return response.data;
  }

  async getEntriesBySlug(entriesSlugs, page = 1) {
    const entriesQuery = entriesSlugs.map((entry) => `ids[]=${entry}`).join('&');
    const url = `${URLS.api.library.bookshelf.entries}?page=${page}&${entriesQuery}`;
    const response = await this._http.get(url);
    return response.data;
  }

  async getEntries(type = null, searchText = null, page = 1) {
    let url = `${ URLS.api.library.bookshelf.entries }?page=${ page }`;
    url = !!type ? `${ url }&type=${ type }` : url;
    url = !!searchText ? `${ url }&search=${ searchText }` : url;
    const response = await this._http.get(url);
    return response.data;
  }

  getJustAddedBookshelfEntries(shouldKeep = false) {
    const bookshelfEntries = LocalStorage.getItem(ADDED_TO_BOOKSHELF_KEY);
    if (!bookshelfEntries) {
      return null;
    }

    if (!shouldKeep) {
      LocalStorage.removeItem(ADDED_TO_BOOKSHELF_KEY);
    }

    try {
      return JSON.parse(bookshelfEntries);
    } catch (error) {
      return null;
    }
  }

  async getUserEntries(userId, type, page = 1) {
    if (!userId
      || (!!type && !ALLOWED_USER_ENTRY_TYPES.includes(type))) {
      throw new Error('Invalid request.');
    }

    let url = `${ URLS.api.userBookshelf.replace('<user-id>', userId) }?page=${ page }`;
    url = (!!type && type !== 'all') ? `${ url }&type=${ type }` : url;
    const response = await this._http.get(url);
    return response.data;
  }

  async markAsCurrentlyReading(entrySlug) {
    const url = URLS.api.library.bookshelf.markAsCurrentlyReading.replace('<entry-slug>', entrySlug);
    const response = await this._http.post(url, null);
    return response.data;
  }

  async markAsRead(entrySlug) {
    const url = URLS.api.library.bookshelf.markAsRead.replace('<entry-slug>', entrySlug);
    const response = await this._http.post(url, null);
    return response.data;
  }

  async markAsWantToRead(entrySlug) {
    const url = URLS.api.library.bookshelf.markAsWantToRead.replace('<entry-slug>', entrySlug);
    const response = await this._http.post(url, null);
    return response.data;
  }

  async moveToBookshelf(entrySlug, bookshelf) {
    switch (bookshelf) {
    case 'currently_reading':
      await this.markAsCurrentlyReading(entrySlug);
      break;
    case 'read':
      await this.markAsRead(entrySlug);
      break;
    case 'want_to_read':
      await this.markAsWantToRead(entrySlug);
      break;
    default:
      throw {
        error: 'Invalid bookshelf',
      };
    }
  }

  async saveEntryReadDate(entrySlug, readDate) {
    const url = URLS.api.library.bookshelf.entry.replace('<entry-slug>', entrySlug);

    const data = {
      bookshelf_entry: {
        marked_as_read: readDate,
      },
    };

    const formData = new FormData();
    addObjectToFormData(data, formData);

    const response = await this._http.patch(url, formData);
    return response.data;
  }

  saveJustAddedBookshelfEntry(bookshelfEntry) {
    const alreadyAddedBookshelfEntries = this.getJustAddedBookshelfEntries(true);

    const bookshelfEntries = alreadyAddedBookshelfEntries
      ? alreadyAddedBookshelfEntries
      : [];

    const existingBookshelfItem = bookshelfEntries.find(b => b === bookshelfEntry.slug);
    if (existingBookshelfItem) {
      return;
    }

    bookshelfEntries.push(bookshelfEntry.slug);
    LocalStorage.setItem(ADDED_TO_BOOKSHELF_KEY, JSON.stringify(bookshelfEntries));
  }
}

export default new BookshelfService();
